/**
 * Review Step Component
 * Step 3 of plan creation wizard - Review & Submit
 */


import { useState } from 'react';
import type { ProjectInfo, ApplicantInfo, WasteStream, OperationalRequirements, ImprovementSuggestionsResponse } from '../../types';
import {
  formatDateShort,
  formatProjectType,
  formatWasteStreamType,
  formatDiversionMethod,
  formatWeight,
  formatPhoneNumber,
} from '../../utils/formatting';
import { ComplianceGauge } from '../compliance/ComplianceGauge';
import { useCalGreen } from '../../hooks/useCalGreen';
import { calculateCompliance } from '../../utils/calgreen';
import { WasteStreamForm } from './WasteStreamForm';

interface ReviewStepProps {
  projectInfo: ProjectInfo;
  applicantInfo: ApplicantInfo;
  wasteStreams: WasteStream[];
  operationalRequirements?: OperationalRequirements;
  improvementSuggestions?: ImprovementSuggestionsResponse;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  onWasteStreamsChange?: (wasteStreams: WasteStream[]) => void;
}

export function ReviewStep({
  projectInfo,
  applicantInfo,
  wasteStreams: initialWasteStreams,
  operationalRequirements,
  improvementSuggestions,
  onSubmit,
  onBack,
  isSubmitting = false,
  onWasteStreamsChange,
}: ReviewStepProps) {
  // State for editable waste streams
  const [wasteStreams, setWasteStreams] = useState<WasteStream[]>(initialWasteStreams);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);
  const [showEditHint, setShowEditHint] = useState(true);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  const compliance = useCalGreen(wasteStreams);
  const [previousCompliance, setPreviousCompliance] = useState(compliance.isCompliant);

  // Helper to determine if project needs operational requirements
  const needsOperationalRequirements =
    projectInfo.project_type === 'commercial_new' ||
    projectInfo.project_type === 'multifamily_new';

  // Update parent component when waste streams change
  const handleWasteStreamsUpdate = (newStreams: WasteStream[]) => {
    setWasteStreams(newStreams);
    if (onWasteStreamsChange) {
      onWasteStreamsChange(newStreams);
    }

    // Check if we just became compliant (wasn't before, is now)
    const newCompliance = calculateCompliance(newStreams);
    const isNowCompliant = newCompliance.meets_calgreen_requirement;
    if (!previousCompliance && isNowCompliant) {
      setShowSuccessAnimation(true);
      setTimeout(() => setShowSuccessAnimation(false), 3000);
    }
    setPreviousCompliance(isNowCompliant);
  };

  const handleEditWasteStream = (index: number) => {
    setEditingIndex(index);
    setShowAddForm(false);
    setShowEditHint(false);
  };

  const handleDeleteWasteStream = (index: number) => {
    const newStreams = wasteStreams.filter((_, i) => i !== index);
    handleWasteStreamsUpdate(newStreams);
  };

  const handleSaveWasteStream = (wasteStream: WasteStream) => {
    if (editingIndex !== null) {
      // Update existing
      const newStreams = [...wasteStreams];
      newStreams[editingIndex] = wasteStream;
      handleWasteStreamsUpdate(newStreams);
      setEditingIndex(null);
    } else {
      // Add new
      handleWasteStreamsUpdate([...wasteStreams, wasteStream]);
      setShowAddForm(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setShowAddForm(false);
  };

  const handleQuickApply = (materialType: string, suggestedMethod: string) => {
    const newStreams = wasteStreams.map((stream) => {
      if (stream.material_type === materialType) {
        return { ...stream, diversion_method: suggestedMethod as WasteStream['diversion_method'] };
      }
      return stream;
    });
    handleWasteStreamsUpdate(newStreams);
  };

  const getDifficultyBadge = (difficulty: 'easy' | 'moderate' | 'challenging') => {
    const styles = {
      easy: 'bg-green-100 text-green-700 border-green-200',
      moderate: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      challenging: 'bg-orange-100 text-orange-700 border-orange-200',
    };
    const labels = {
      easy: 'Easy',
      moderate: 'Moderate',
      challenging: 'Challenging',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[difficulty]}`}>
        {labels[difficulty]}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">Review & Submit</h3>
        <p className="text-sm text-neutral-600">
          Review your AI-generated plan below. You can edit any waste stream by clicking on it to improve your diversion rate.
        </p>
      </div>

      {/* Edit Hint Banner - dismissible */}
      {showEditHint && wasteStreams.length > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-900">Review and Edit Your Plan</p>
                <p className="text-sm text-blue-700 mt-1">
                  Click any waste stream below to edit quantities or diversion methods. Add or remove items to optimize your diversion rate.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowEditHint(false)}
              className="text-blue-600 hover:text-blue-700 ml-4"
              aria-label="Dismiss hint"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Success Animation - when reaching compliance */}
      {showSuccessAnimation && (
        <div className="p-4 bg-green-50 border-2 border-green-500 rounded-lg animate-bounce">
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-lg font-bold text-green-900">Congratulations!</p>
              <p className="text-sm text-green-700">You&apos;ve achieved CalGreen 65% compliance!</p>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Gauge */}
      <ComplianceGauge
        diversionRate={compliance.diversion_rate}
        totalGenerated={compliance.total_waste_generated}
        totalDiverted={compliance.total_waste_diverted}
        isCompliant={compliance.isCompliant}
      />

      {/* Improvement Suggestions - only show if not compliant */}
      {!compliance.isCompliant && improvementSuggestions && improvementSuggestions.suggestions.length > 0 && (
        <div className="border border-blue-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setSuggestionsExpanded(!suggestionsExpanded)}
            className="w-full p-4 bg-blue-50 flex items-center justify-between hover:bg-blue-100 transition-colors"
          >
            <div className="flex items-start gap-3 flex-1 text-left">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <div>
                <h4 className="font-semibold text-blue-900">Improvement Suggestions</h4>
                <p className="text-sm text-blue-700 mt-1">
                  {improvementSuggestions.message} You need to divert an additional <strong>{formatWeight(improvementSuggestions.gap_tons)}</strong> ({(improvementSuggestions.target_rate - improvementSuggestions.current_rate).toFixed(1)}% more) to meet the 65% CalGreen requirement.
                </p>
              </div>
            </div>
            <svg
              className={`w-5 h-5 text-blue-600 transition-transform ${suggestionsExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {suggestionsExpanded && (
            <div className="p-4 space-y-3 bg-white">
              {improvementSuggestions.suggestions.map((suggestion, index) => (
                <div key={index} className="border border-neutral-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h5 className="font-semibold text-neutral-900">
                          {formatWasteStreamType(suggestion.material_type)}
                        </h5>
                        {getDifficultyBadge(suggestion.difficulty)}
                      </div>
                      <div className="text-sm text-neutral-600 space-y-1">
                        <div>
                          Current: <strong>{formatWeight(suggestion.current_quantity)}</strong> going to{' '}
                          <strong>{formatDiversionMethod(suggestion.current_method)}</strong>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          <span>
                            Suggested: <strong>{formatDiversionMethod(suggestion.suggested_method)}</strong>
                          </span>
                        </div>
                        <div className="text-accent-600 font-medium">
                          Potential impact: +{formatWeight(suggestion.potential_diversion)} diverted
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 mb-3 italic">
                    {suggestion.rationale}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleQuickApply(suggestion.material_type, suggestion.suggested_method)}
                    className="btn-secondary text-sm"
                  >
                    Quick Apply
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Warning if not compliant and no suggestions */}
      {!compliance.isCompliant && (!improvementSuggestions || improvementSuggestions.suggestions.length === 0) && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <h4 className="font-semibold text-red-900 mb-1">
                Does Not Meet CalGreen Requirements
              </h4>
              <p className="text-sm text-red-700">
                Your plan currently has a {compliance.diversion_rate.toFixed(1)}% diversion rate.
                CalGreen 5.408 requires a minimum of {compliance.threshold}%. You can still save
                this plan as a draft and edit it later.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Project Information */}
      <div className="card">
        <h4 className="font-semibold text-neutral-900 mb-4">Project Information</h4>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-neutral-600 mb-1">Project Name</dt>
            <dd className="text-neutral-900 font-medium">{projectInfo.project_name}</dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Project Type</dt>
            <dd className="text-neutral-900 font-medium">
              {formatProjectType(projectInfo.project_type)}
            </dd>
          </div>
          <div className="md:col-span-2">
            <dt className="text-neutral-600 mb-1">Address</dt>
            <dd className="text-neutral-900 font-medium">{projectInfo.project_address}</dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Start Date</dt>
            <dd className="text-neutral-900 font-medium">
              {formatDateShort(projectInfo.estimated_start_date)}
            </dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Completion Date</dt>
            <dd className="text-neutral-900 font-medium">
              {formatDateShort(projectInfo.estimated_completion_date)}
            </dd>
          </div>
          {projectInfo.total_square_footage && (
            <div>
              <dt className="text-neutral-600 mb-1">Square Footage</dt>
              <dd className="text-neutral-900 font-medium">
                {projectInfo.total_square_footage.toLocaleString()} sq ft
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Applicant Information */}
      <div className="card">
        <h4 className="font-semibold text-neutral-900 mb-4">Applicant Information</h4>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-neutral-600 mb-1">Contact Name</dt>
            <dd className="text-neutral-900 font-medium">{applicantInfo.contact_name}</dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Contact Email</dt>
            <dd className="text-neutral-900 font-medium">{applicantInfo.contact_email}</dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Contact Phone</dt>
            <dd className="text-neutral-900 font-medium">
              {formatPhoneNumber(applicantInfo.contact_phone)}
            </dd>
          </div>
          {applicantInfo.company_name && (
            <div>
              <dt className="text-neutral-600 mb-1">Company Name</dt>
              <dd className="text-neutral-900 font-medium">{applicantInfo.company_name}</dd>
            </div>
          )}
          {applicantInfo.license_number && (
            <div>
              <dt className="text-neutral-600 mb-1">License Number</dt>
              <dd className="text-neutral-900 font-medium">{applicantInfo.license_number}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Waste Streams */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-neutral-900">
              Waste Streams ({wasteStreams.length})
            </h4>
            <div className="group relative">
              <svg className="w-4 h-4 text-neutral-400 hover:text-neutral-600 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="absolute left-0 top-6 w-64 p-2 bg-neutral-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                Click any waste stream to edit quantities or diversion methods. Changes update your compliance rate in real-time.
              </div>
            </div>
          </div>
        </div>

        {wasteStreams.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-yellow-900">No Waste Streams</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Add waste streams below to create your waste management plan.
                </p>
              </div>
            </div>
          </div>
        ) : editingIndex !== null ? (
          // Edit Form
          <div className="mb-4 p-4 bg-neutral-50 rounded-lg">
            <h5 className="font-semibold text-neutral-900 mb-4">Edit Waste Stream</h5>
            <WasteStreamForm
              initialData={wasteStreams[editingIndex]}
              onSubmit={handleSaveWasteStream}
              onCancel={handleCancelEdit}
            />
          </div>
        ) : (
          // Waste Streams List
          <div className="space-y-3 mb-4">
            {wasteStreams.map((stream, index) => (
              <div
                key={index}
                className="flex justify-between items-start p-4 bg-neutral-50 rounded-lg border-2 border-transparent hover:border-primary-200 hover:bg-primary-50 transition-all cursor-pointer group"
                onClick={() => handleEditWasteStream(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleEditWasteStream(index);
                  }
                }}
                aria-label={`Edit ${formatWasteStreamType(stream.material_type)} waste stream`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="font-semibold text-neutral-900">
                      {formatWasteStreamType(stream.material_type)}
                    </div>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        stream.diversion_method === 'landfill'
                          ? 'bg-neutral-100 text-neutral-700'
                          : 'bg-accent-100 text-accent-700'
                      }`}
                    >
                      {formatDiversionMethod(stream.diversion_method)}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-600">
                    Quantity: {formatWeight(stream.estimated_quantity)}
                  </div>
                  {stream.facility_name && (
                    <div className="text-sm text-neutral-600 mt-1">
                      Facility: {stream.facility_name}
                    </div>
                  )}
                  <div className="text-xs text-primary-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    Click to edit
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditWasteStream(index);
                    }}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium px-3 py-1 rounded hover:bg-primary-100 transition-colors"
                    aria-label={`Edit ${formatWasteStreamType(stream.material_type)}`}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`Delete ${formatWasteStreamType(stream.material_type)} waste stream?`)) {
                        handleDeleteWasteStream(index);
                      }
                    }}
                    className="text-red-600 hover:text-red-700 text-sm font-medium px-3 py-1 rounded hover:bg-red-100 transition-colors"
                    aria-label={`Delete ${formatWasteStreamType(stream.material_type)}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Waste Stream Button/Form */}
        {showAddForm ? (
          <div className="p-4 bg-neutral-50 rounded-lg">
            <h5 className="font-semibold text-neutral-900 mb-4">Add Waste Stream</h5>
            <WasteStreamForm
              onSubmit={handleSaveWasteStream}
              onCancel={handleCancelEdit}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setShowAddForm(true);
              setEditingIndex(null);
            }}
            className="btn-secondary w-full flex items-center justify-center gap-2"
            disabled={editingIndex !== null}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Waste Stream
          </button>
        )}
      </div>

      {/* Operational Requirements - Only for commercial-new and multifamily-new */}
      {needsOperationalRequirements && operationalRequirements && (
        <div className="card">
          <h4 className="font-semibold text-neutral-900 mb-4">Operational Requirements</h4>
          <p className="text-sm text-neutral-600 mb-4">
            CalGreen-compliant waste management specifications for ongoing operations
          </p>

          {/* Container Specifications */}
          <div className="mb-6">
            <h5 className="text-sm font-semibold text-neutral-800 mb-3">Container Specifications</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-neutral-50 rounded-lg p-4">
                <div className="text-sm text-neutral-600 mb-1">Trash</div>
                <div className="font-medium text-neutral-900">
                  {operationalRequirements.containers.trash.count} x {operationalRequirements.containers.trash.size} {operationalRequirements.containers.trash.unit}
                </div>
              </div>
              <div className="bg-neutral-50 rounded-lg p-4">
                <div className="text-sm text-neutral-600 mb-1">Recycling</div>
                <div className="font-medium text-neutral-900">
                  {operationalRequirements.containers.recycling.count} x {operationalRequirements.containers.recycling.size} {operationalRequirements.containers.recycling.unit}
                </div>
              </div>
              <div className="bg-neutral-50 rounded-lg p-4">
                <div className="text-sm text-neutral-600 mb-1">Organics</div>
                <div className="font-medium text-neutral-900">
                  {operationalRequirements.containers.organics.count} x {operationalRequirements.containers.organics.size} {operationalRequirements.containers.organics.unit}
                </div>
              </div>
            </div>
          </div>

          {/* Enclosure Specifications */}
          <div className="mb-6">
            <h5 className="text-sm font-semibold text-neutral-800 mb-3">Enclosure Dimensions</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="text-xs text-neutral-600 mb-1">Minimum Width</div>
                <div className="font-medium text-neutral-900">{operationalRequirements.enclosure.minWidth} ft</div>
              </div>
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="text-xs text-neutral-600 mb-1">Minimum Depth</div>
                <div className="font-medium text-neutral-900">{operationalRequirements.enclosure.minDepth} ft</div>
              </div>
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="text-xs text-neutral-600 mb-1">Minimum Height</div>
                <div className="font-medium text-neutral-900">{operationalRequirements.enclosure.minHeight} ft</div>
              </div>
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="text-xs text-neutral-600 mb-1">Gate Width</div>
                <div className="font-medium text-neutral-900">{operationalRequirements.enclosure.gateWidth} ft</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="text-xs text-neutral-600 mb-1">Overhead Clearance</div>
                <div className="font-medium text-neutral-900">{operationalRequirements.enclosure.overheadClearance} ft</div>
              </div>
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="text-xs text-neutral-600 mb-1">Approach Distance</div>
                <div className="font-medium text-neutral-900">{operationalRequirements.enclosure.approachDistance} ft</div>
              </div>
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="text-xs text-neutral-600 mb-1">Fire Clearance</div>
                <div className="font-medium text-neutral-900">{operationalRequirements.enclosure.fireClearance} ft</div>
              </div>
            </div>
          </div>

          {/* Accessibility Requirements */}
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-neutral-800 mb-3">Accessibility Specifications</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-neutral-50 rounded-lg p-4">
                <div className="text-sm text-neutral-600 mb-1">Path Width</div>
                <div className="font-medium text-neutral-900">{operationalRequirements.accessibility.pathWidth} inches</div>
              </div>
              <div className="bg-neutral-50 rounded-lg p-4">
                <div className="text-sm text-neutral-600 mb-1">Reach Height</div>
                <div className="font-medium text-neutral-900">{operationalRequirements.accessibility.reachHeight} inches</div>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          {operationalRequirements.recommendations && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="text-sm font-semibold text-blue-900 mb-2">Recommendations</h5>
              <p className="text-sm text-blue-800">{operationalRequirements.recommendations}</p>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t border-neutral-200">
        <button
          type="button"
          onClick={onBack}
          className="btn-secondary"
          disabled={isSubmitting}
        >
          Back
        </button>
        <button
          type="button"
          onClick={onSubmit}
          className="btn-primary flex-1"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? 'Generating Plan...' : 'Generate Plan'}
        </button>
      </div>
    </div>
  );
}